import { useEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import {
  Table,
  useQueryState,
  Filters,
  useFilters,
  useGetList,
  usePrevious,
  PermissionEnum,
  SchemaFilterType,
  formatDate,
  formatBalance,
  PermissionDenied,
  SelectItemProps,
  IconDownload,
  ButtonSize,
  IconAnonymized,
  ButtonSecondary,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  setTakeCount as setStoreTakeCount,
  clearPaymentOrdersBeneficiariesList,
} from './store/slices/list';
import { setNotificationVisible } from '../../../../../store/slice';
import {
  columnsItems,
  columnsLabels,
  schema,
  filtersSchema,
} from './variables/orders';
import {
  useLazyGetPaymentsOrdersBeneficiariesQuery,
  useExportOrdersTransactionMutation,
} from './store/services';
import { Order, StatusTitle } from './types';
import useGetAccountsAutocompleteList from './use-accounts-autocomplete';

import cn from './styles.module.css';

const formatPaymentOrdersList = ({ list }: { list: Order[] }) => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, any> = {
      createdAt: (
        <div>
          {result.createdAt ? formatDate(result.createdAt || '', true) : '-'}
        </div>
      ),
      orderDate: (
        <div>
          {result.orderDate ? formatDate(result.orderDate || '', true) : '-'}
        </div>
      ),
      orderCreatedAt: (
        <div>
          {result?.paymentOrder?.orderCreatedAt
            ? formatDate(result?.paymentOrder?.orderCreatedAt || '', true)
            : '-'}
        </div>
      ),
      orderAcceptedAt: (
        <div>
          {result?.paymentOrder?.orderAcceptedAt
            ? formatDate(result?.paymentOrder?.orderAcceptedAt, true)
            : '-'}
        </div>
      ),
      senderDob: (
        <div>
          {result.senderDob === '%Anonymized%' ? (
            <IconAnonymized />
          ) : (
            formatDate(result.senderDob)
          )}
        </div>
      ),
      senderName: (
        <div>
          {result.senderName === '%Anonymized%' ? (
            <IconAnonymized />
          ) : (
            result.senderName
          )}
        </div>
      ),
      recepientAccountId: (
        <>
          <div>{result?.paymentOrder?.recepientAccount?.description}</div>
          <div className={cn.accNumber}>
            {result?.paymentOrder?.recepientAccount?.fullNumber}
          </div>
        </>
      ),
      localCountryId: <div>{result.localCountry.name}</div>,
      localCurrencyId: <div>{result.localCurrency.name}</div>,
      localAmount: (
        <div>
          {formatBalance(Number(result.localAmount), result?.localCurrency)}
        </div>
      ),
      destCountryId: <div>{result?.destCountry.name}</div>,
      destCurrencyId: <div>{result.destCurrency.name}</div>,
      orderStatus: (
        // @ts-ignore
        <div>{StatusTitle[item.paymentOrder?.status as StatusTitle]}</div>
      ),
      destAmount: (
        <div>
          {formatBalance(Number(result.destAmount), result?.destCurrency)}
        </div>
      ),
      beneficiaryName: (
        <div>
          {result.beneficiaryName === '%Anonymized%' ? (
            <IconAnonymized />
          ) : (
            result.beneficiaryName
          )}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const CashCollectionOrders = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    transactionsDictionaries: { currencies, activePermissions, countries },
  } = useAppSelector((state) => state);

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);
  const dispatch = useAppDispatch();
  const {
    root: { userId },
    cashCollection: {
      list: { cashCollectionOrders, cashCollectionOrdersPagination, takeCount },
    },
  } = useAppSelector((state) => state);

  const prevFilters = usePrevious(filters);

  const [getPaymentsOrdersBeneficiaries, { isLoading, isFetching }] =
    useLazyGetPaymentsOrdersBeneficiariesQuery();

  const [
    exportOrders,
    { isSuccess: exportIsSuccess, isLoading: exportIsLoading },
  ] = useExportOrdersTransactionMutation();

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<Order>({
      getList: getPaymentsOrdersBeneficiaries,
      onFilterChange,
      onSortChange,
      schema,
    });

  const {
    autocompleteList: recipientAccounts,
    onSearch: onSearchRecipientAccounts,
    onClear,
  } = useGetAccountsAutocompleteList('&isClientAccount=true');

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    if (name === 'recepientAccountId') {
      onSearchRecipientAccounts(searchAutocomplete);
    }
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  const formattedData = useMemo(() => {
    const data = formatPaymentOrdersList({
      list: cashCollectionOrders as Order[],
    });

    return data;
  }, [cashCollectionOrders]);

  const onExportClick = async () => {
    const exportFilters: Record<string, string | number | string[]> = {
      types: ['CASH_COLLECTION_SINGLE_OUTPUT'],
    };

    if (filters && Object.keys(filters)?.length) {
      Object.keys(filters)?.forEach((item) => {
        if (item !== 'cursor' && item !== 'take') {
          if (item !== 'cursor') {
            if (item === 'paymentOrderStatus') {
              exportFilters[item] = filters[item].value;
            } else if (item === 'orderCreatedAt') {
              const orderCreatedAt = filters[item].value;

              const [from, to] = orderCreatedAt;

              exportFilters.orderCreatedAtFrom = from;

              if (to) {
                exportFilters.orderCreatedAtTo = to;
              }
            } else if (item === 'createdAt') {
              const orderCreatedAt = filters[item].value;

              const [from, to] = orderCreatedAt;

              exportFilters.createdAtFrom = from;

              if (to) {
                exportFilters.createdAtTo = to;
              }
            } else if (item === 'orderAcceptedAt') {
              const orderAcceptedAt = filters[item].value;

              const [from, to] = orderAcceptedAt;

              exportFilters.orderAcceptedAtFrom = from;

              if (to) {
                exportFilters.orderAcceptedAtTo = to;
              }
            } else {
              const [filterValue] = filters[item].value;

              exportFilters[item] = filterValue;
            }
          }
        }
      });
    }

    await exportOrders(exportFilters);
  };

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        dispatch(setStoreTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'payments-orders-beneficiaries',
        }),
      );
    }
  }, [exportIsSuccess]);

  useEffect(() => {
    setValues('localCountryId', countries as SelectItemProps[]);
    setValues('localCurrencyId', currencies as SelectItemProps[]);
    setValues('destCountryId', countries as SelectItemProps[]);
    setValues('destCurrencyId', currencies as SelectItemProps[]);
    setValues('destCurrencyId', currencies as SelectItemProps[]);
    setValues('recepientAccountId', recipientAccounts);
  }, [currencies, countries, recipientAccounts]);

  useEffect(() => {
    if (
      takeCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !cashCollectionOrders &&
      sort &&
      sort.sortProp &&
      activePermissions
    ) {
      if (
        activePermissions?.includes(
          PermissionEnum.PAYMENTS_ORDERS_BENEFICIARIES_LIST,
        )
      ) {
        const filtersValues: Filters = {
          ...filters,
          take: {
            value: [(takeCount as number)?.toString()],
            type: SchemaFilterType.STRING,
          },
        };

        if (
          filtersValues?.senderDob &&
          filtersValues?.senderDob?.value?.length
        ) {
          filtersValues.senderDob = {
            value: [new Date(filtersValues.senderDob?.value[0]).toDateString()],
            type: SchemaFilterType.STRING,
          };
        }

        getListAction({
          filters: filtersValues,
          sort,
        });
      }
    }
  }, [filters, sort, cashCollectionOrders, takeCount, activePermissions]);

  useEffect(
    () => () => {
      dispatch(clearPaymentOrdersBeneficiariesList());
    },
    [dispatch],
  );

  let content;

  if (
    activePermissions?.includes(
      PermissionEnum.PAYMENTS_ORDERS_BENEFICIARIES_LIST,
    )
  ) {
    content = (
      <div className={cn.cashCollection} ref={scrollRef}>
        <Table
          columns={columnsItems}
          data={formattedData || []}
          onSort={(name) => {
            onSort(name);
          }}
          filters={filters}
          filtersComponent={filtersComponent}
          sort={sort}
          columnsLabels={columnsLabels}
          onFilterChange={(values) => {
            const nextFilters: Filters = { ...values };

            if (
              nextFilters.senderDob &&
              nextFilters?.senderDob?.value?.length
            ) {
              nextFilters.senderDob = {
                value: [
                  new Date(nextFilters.senderDob?.value[0]).toDateString(),
                ],
                type: SchemaFilterType.STRING,
              };
            }

            onFilter({
              ...nextFilters,
              take: {
                value: [(takeCount as number)?.toString()],
                type: SchemaFilterType.STRING,
              },
            });
          }}
          handleSubmit={handleSubmit}
          isLoading={isLoading || takeCount === 0 || isFetching}
          take={(takeCount as number)?.toString()}
          onClearAutoComplete={() => onClear()}
          setPage={(data) => {
            const nextFilters: Filters = {
              ...filters,
            };

            if (
              nextFilters.senderDob &&
              nextFilters?.senderDob?.value?.length
            ) {
              nextFilters.senderDob = {
                value: [
                  new Date(nextFilters.senderDob?.value[0]).toDateString(),
                ],
                type: SchemaFilterType.STRING,
              };
            }

            setPage({
              ...data,
              filters: {
                ...nextFilters,
                take: {
                  value: [(takeCount as number)?.toString()],
                  type: SchemaFilterType.STRING,
                },
              },
            });
          }}
          tableFormClassName={cn.cashCollectionOrdersTableForm}
          pagination={cashCollectionOrdersPagination}
          topContent={
            <div>
              {activePermissions?.includes(
                PermissionEnum.PAYMENTS_ORDERS_BENEFICIARIES_LIST,
              ) && (
                <ButtonSecondary
                  text={exportIsLoading ? 'Loading...' : 'Export orders'}
                  icon={<IconDownload />}
                  className={`button-white ${cn.exportButton} ${cn.exportOrdersButton}`}
                  size={ButtonSize.SMALL}
                  onClick={onExportClick}
                />
              )}
            </div>
          }
          error={errorList}
          paginationDisabled={isFetching || isLoading}
          isFetching={isFetching}
          userId={userId}
          withScroll
          clickable
        />
        <Outlet />
      </div>
    );
  } else {
    content = <PermissionDenied />;
  }

  return content;
};

export default CashCollectionOrders;
